import { Injectable } from '@angular/core';
import { filterNilValue, Order, QueryConfig, QueryEntity } from '@datorama/akita';
import { MarketDataState, MarketDataStore } from './marketdata.store';
import { map, pluck } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ExtendedMarketData, MarketData } from './marketdata.model';

@Injectable({ providedIn: 'root' })
@QueryConfig({
    sortBy: 'date',
    sortByOrder: Order.DESC
})
export class MarketDataQuery extends QueryEntity<MarketDataState> {
    // Sorted descending - first element in array will be "latest valuation"
    latestValuation$ = this.selectAll().pipe(
        map((marketDatas) => (marketDatas ? marketDatas[0] : <MarketData>{}))
    );
    latestTotalShares$ = this.selectAll({
        filterBy: (entity) => !!entity.shares,
        sortBy: 'date',
        sortByOrder: Order.DESC
    }).pipe(
        map((valuation) => valuation[0] || { shares: null }),
        pluck('shares')
    );

    /** all$ - all market data with added shares info (using last available value)
     * and calculated total value and change since initial value
     * TODO: calculations should be done server-side
     */
    all$: Observable<Array<ExtendedMarketData>> = this.selectAll({
        sortBy: 'date',
        sortByOrder: Order.ASC
    }).pipe(
        map((valuations) =>
            valuations
                .reduce((previous, current, index) => {
                    return [
                        ...previous,
                        {
                            ...current,
                            shares:
                                current?.shares || previous[index - 1]?.shares || null,
                            diluted:
                                current?.diluted || previous[index - 1]?.diluted || null,
                            totalValue:
                                current?.price *
                                    (current?.shares || previous[index - 1]?.shares) ||
                                null
                        }
                    ];
                }, [])
                .sort((a, b) => {
                    if (a.date < b.date) {
                        return 1;
                    }
                    if (a.date > b.date) {
                        return -1;
                    }
                    // names must be equal
                    return 0;
                })
        ),
        map((valuations) => {
            const lastIndex = valuations.length - 1;
            const baseValue = valuations[lastIndex]?.totalValue;
            return valuations.map((valuation, index) => ({
                ...valuation,
                change:
                    baseValue && valuation?.totalValue
                        ? valuation.totalValue / baseValue - 1
                        : lastIndex === index
                        ? 0
                        : null
            }));
        })
    );

    constructor(protected store: MarketDataStore) {
        super(store);
    }
}
